import React, { Suspense, useEffect, useRef } from 'react';
import {
    Navigate,
    Outlet,
    RouterProvider,
    createBrowserRouter,
    useLocation,
} from 'react-router-dom';

import { AuthGuard } from '@/components/AuthGuard';
import { EmbedTokenGate } from '@/components/EmbedTokenGate';
import { RouteChunkFallback } from '@/components/RouteChunkFallback';
import { useAuthStore } from '@/store/authStore';
import { clearEmbedApiToken } from '@/utils/embedApiToken';
import { getFirstAccessibleRoute } from '@/utils/sidebarNav';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { usePermissions } from '@ai-content-hub/hooks';

// EAA-1670: code-split the two layouts and the login page too, so the initial
// bundle is just the router shell + guards. Auth/embed guards stay eager —
// they make synchronous redirect decisions before any layout mounts. The
// RootLayout Suspense below catches these chunks; each layout keeps its own
// inner Suspense for its lazy page children.
//
// EAA-1670: use lazyWithRetry (not React.lazy) so a navigation that hits a
// chunk deleted by a fresh deploy self-heals via one guarded full reload
// instead of white-screening. The 2nd arg is a stable per-chunk reload-guard key.
const MainLayout = lazyWithRetry(
    () => import('@/layouts/MainLayout').then((m) => ({ default: m.MainLayout })),
    'MainLayout'
);
const IframeLayout = lazyWithRetry(
    () => import('@/layouts/IframeLayout').then((m) => ({ default: m.IframeLayout })),
    'IframeLayout'
);
const LoginPage = lazyWithRetry(
    () => import('@/pages/Login').then((m) => ({ default: m.LoginPage })),
    'LoginPage'
);
// EAA-1762: OIDC redirect-callback route (Keycloak Authorization Code + PKCE).
const CallbackPage = lazyWithRetry(
    () => import('@/pages/Callback').then((m) => ({ default: m.CallbackPage })),
    'CallbackPage'
);

const DashboardPage = lazyWithRetry(
    () => import('@/pages/Dashboard').then((m) => ({ default: m.DashboardPage })),
    'DashboardPage'
);
const ContentBankPage = lazyWithRetry(() => import('@/pages/ContentBank'), 'ContentBankPage');
const ContentDetailsPage = lazyWithRetry(
    () => import('@/pages/ContentDetails').then((m) => ({ default: m.ContentDetailsPage })),
    'ContentDetailsPage'
);
const ContentTypesPage = lazyWithRetry(
    () => import('@/pages/ContentTypes').then((m) => ({ default: m.ContentTypesScreen })),
    'ContentTypesPage'
);
const TagTypesPage = lazyWithRetry(
    () => import('@/pages/TagTypes').then((m) => ({ default: m.TagTypesPage })),
    'TagTypesPage'
);
const TagsPage = lazyWithRetry(
    () => import('@/pages/Tags').then((m) => ({ default: m.TagsPage })),
    'TagsPage'
);
const TenantsPage = lazyWithRetry(
    () => import('@/pages/Tenants').then((m) => ({ default: m.TenantsPage })),
    'TenantsPage'
);
const TenantLayout = lazyWithRetry(
    () => import('@/pages/Tenants').then((m) => ({ default: m.TenantLayout })),
    'TenantLayout'
);
const TenantDashboardPage = lazyWithRetry(
    () => import('@/pages/Tenants').then((m) => ({ default: m.TenantDashboardPage })),
    'TenantDashboardPage'
);
const TenantUsersPage = lazyWithRetry(
    () => import('@/pages/Tenants').then((m) => ({ default: m.TenantUsersPage })),
    'TenantUsersPage'
);
const TenantPermissionsPage = lazyWithRetry(
    () => import('@/pages/Tenants').then((m) => ({ default: m.TenantPermissionsPage })),
    'TenantPermissionsPage'
);
const TenantTokensPage = lazyWithRetry(
    () => import('@/pages/Tenants').then((m) => ({ default: m.TenantTokensPage })),
    'TenantTokensPage'
);
const SyncSystemsPage = lazyWithRetry(
    () =>
        import('@/pages/Tenants/SyncManagement/SyncSystemsPage').then((m) => ({
            default: m.SyncSystemsPage,
        })),
    'SyncSystemsPage'
);
const ProgramsPage = lazyWithRetry(
    () => import('@/pages/Programs').then((m) => ({ default: m.ProgramsPage })),
    'ProgramsPage'
);
const ProgramCreatePage = lazyWithRetry(
    () => import('@/pages/Programs').then((m) => ({ default: m.ProgramCreatePage })),
    'ProgramCreatePage'
);
const ProgramDetailPage = lazyWithRetry(
    () => import('@/pages/Programs').then((m) => ({ default: m.ProgramDetailPage })),
    'ProgramDetailPage'
);
const ProgramManualCreatePage = lazyWithRetry(
    () => import('@/pages/Programs').then((m) => ({ default: m.ProgramManualCreatePage })),
    'ProgramManualCreatePage'
);
const ThemeEditorPage = lazyWithRetry(
    () => import('@/pages/ThemeEditor').then((m) => ({ default: m.ThemeEditorPage })),
    'ThemeEditorPage'
);
// EAA-1212: platform system settings (platform staff only — the page guards itself too).
const SystemSettingsPage = lazyWithRetry(
    () => import('@/pages/SystemSettings').then((m) => ({ default: m.SystemSettingsPage })),
    'SystemSettingsPage'
);
const DocumentsPage = lazyWithRetry(
    () => import('@/pages/Documents').then((m) => ({ default: m.DocumentsPage })),
    'DocumentsPage'
);

const EmbedRouteSync: React.FC = () => {
    const location = useLocation();
    const embedSessionEnded = useAuthStore((s) => s.embedSessionEnded);
    const prevPathRef = useRef(location.pathname);

    useEffect(() => {
        const prev = prevPathRef.current;
        prevPathRef.current = location.pathname;
        if (prev.startsWith('/embed') && !location.pathname.startsWith('/embed')) {
            clearEmbedApiToken();
            embedSessionEnded();
        }
    }, [location.pathname, embedSessionEnded]);

    return null;
};

/**
 * Guard that redirects tenant-portal users away from the admin dashboard.
 */
const DashboardGuard: React.FC = () => {
    const authMode = useAuthStore((s) => s.authMode);
    const user = useAuthStore((s) => s.user);
    const { hasScreenAccess } = usePermissions();

    if (authMode === 'tenant' && user?.tenant?.id) {
        const target = getFirstAccessibleRoute(hasScreenAccess, `/tenants/${user.tenant.id}`);
        return <Navigate to={target} replace />;
    }

    return <DashboardPage />;
};

/**
 * Top layout: runs embed path sync on every navigation (must live under the router).
 */
const RootLayout: React.FC = () => (
    <>
        <EmbedRouteSync />
        {/* EAA-1670: top-level boundary for the lazy layout/login chunks. Page
            chunks are caught deeper by each layout's own inner Suspense. */}
        <Suspense fallback={<RouteChunkFallback />}>
            <Outlet />
        </Suspense>
    </>
);

const router = createBrowserRouter([
    {
        path: '/',
        element: <RootLayout />,
        children: [
            { path: 'login', element: <LoginPage /> },
            { path: 'callback', element: <CallbackPage /> },
            {
                path: 'embed',
                element: (
                    <EmbedTokenGate>
                        <IframeLayout />
                    </EmbedTokenGate>
                ),
                children: [
                    { path: 'tags', element: <TagsPage /> },
                    { path: 'tag-types', element: <TagTypesPage /> },
                    { index: true, element: <Navigate to="/embed/tags" replace /> },
                    { path: '*', element: <Navigate to="/embed/tags" replace /> },
                ],
            },
            {
                element: (
                    <AuthGuard>
                        <MainLayout />
                    </AuthGuard>
                ),
                children: [
                    { index: true, element: <Navigate to="/programs" replace /> },
                    { path: 'dashboard', element: <DashboardGuard /> },
                    { path: 'question-bank', element: <ContentBankPage /> },
                    { path: 'question-bank/new', element: <ContentDetailsPage /> },
                    { path: 'question-bank/:contentId', element: <ContentDetailsPage /> },
                    { path: 'content-types', element: <ContentTypesPage /> },
                    // EAA-2026: the question-type groups are a tab of /content-types now; the
                    // earlier standalone route redirects to that tab.
                    {
                        path: 'content-types/question-type-groups',
                        element: <Navigate to="/content-types?tab=question-type-groups" replace />,
                    },
                    { path: 'tag-types', element: <TagTypesPage /> },
                    { path: 'tags', element: <TagsPage /> },
                    { path: 'programs/create', element: <ProgramManualCreatePage /> },
                    { path: 'programs/new', element: <ProgramCreatePage /> },
                    {
                        path: 'programs/:programId/edit-manual',
                        element: <ProgramManualCreatePage />,
                    },
                    { path: 'programs/:programId', element: <ProgramDetailPage /> },
                    { path: 'programs', element: <ProgramsPage /> },
                    { path: 'documents', element: <DocumentsPage /> },
                    { path: 'tenants', element: <TenantsPage /> },
                    {
                        path: 'tenants/:tenantId',
                        element: <TenantLayout />,
                        children: [
                            { index: true, element: <TenantDashboardPage /> },
                            { path: 'sync-management/systems', element: <SyncSystemsPage /> },
                            { path: 'users', element: <TenantUsersPage /> },
                            { path: 'permissions', element: <TenantPermissionsPage /> },
                            { path: 'tokens', element: <TenantTokensPage /> },
                        ],
                    },
                    { path: 'settings/theme', element: <ThemeEditorPage /> },
                    { path: 'settings/system', element: <SystemSettingsPage /> },
                ],
            },
            { path: '*', element: <Navigate to="/programs" replace /> },
        ],
    },
]);

export const AppRoutes: React.FC = () => <RouterProvider router={router} />;
